import { Beaker, Hourglass, Scale } from "lucide-react";

const BELIEFS = [
  {
    icon: Beaker,
    eyebrow: "01 · Measured",
    title: "Matcha should be measured, not guessed.",
    body:
      "Most counters treat matcha as one undifferentiated green powder. We treat it the way a lab treats two different reagents — distinct grades, distinct purposes, never substituted to protect a margin.",
  },
  {
    icon: Hourglass,
    eyebrow: "02 · Slow",
    title: "Slow is a feature, not a delay.",
    body:
      "Every bowl is whisked to order with a bamboo chasen. No batching, no pre-mixing. The few minutes it takes to prepare a proper bowl are part of the product — not friction to be engineered away.",
  },
  {
    icon: Scale,
    eyebrow: "03 · Honest",
    title: "Pricing should be honest.",
    body:
      "If a grade is expensive to source, it's priced like it's expensive to source. Uji specialty stays full-price always — including under every pass and the founding membership — because discounting it would mean either losing money or quietly downgrading the cup.",
  },
];

const ABOUT_IMG = "https://images.unsplash.com/photo-1624893464449-22a7e40de7c9?crop=entropy&cs=srgb&fm=jpg";

export default function Philosophy() {
  return (
    <section id="philosophy" className="px-6 md:px-10 py-24 md:py-36" data-testid="philosophy-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 items-start mb-20">
          <div className="md:col-span-5">
            <p className="overline text-[#C47253] mb-6">本当 · Hōnto means &quot;true&quot;</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#1A2B22]">
              Matcha treated as something to <em className="text-[#2C4C3B]">get right</em>,
              not something to romanticise.
            </h2>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <p className="text-[#1A2B22] text-base md:text-lg leading-relaxed">
              <span className="font-serif italic text-[#2C4C3B]">Aishwarya Rawat</span> started hōnto with a research
              background, not a hospitality one. It shows up in how the counter is run. Where most matcha cafés sell
              a vibe, hōnto sells a measurement — the grade in your cup is named, the source is specific, and the
              price reflects what that grade actually costs to bring in.
            </p>
            <p className="mt-5 text-[#5C7365] text-base leading-relaxed">
              Nothing is dressed up to seem more premium than it is, and nothing is cut to seem cheaper than it
              should be. Direct, a little dry, allergic to overstatement — we say what&apos;s true and let the specifics
              (the cultivar, the region, the first harvest) do the persuading.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {BELIEFS.map((b) => {
            const Icon = b.icon;
            return (
              <article
                key={b.eyebrow}
                className="rounded-3xl bg-background border border-border p-7 md:p-9 hover:border-[#2C4C3B]/40 transition-all duration-500 hover:-translate-y-1"
                data-testid={`belief-${b.eyebrow.split(" ")[0]}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#EBEFDE] flex items-center justify-center mb-7">
                  <Icon className="w-5 h-5 text-[#2C4C3B]" strokeWidth={1.5} />
                </div>
                <p className="overline text-[#C47253] mb-3">{b.eyebrow}</p>
                <h3 className="font-serif text-2xl md:text-[1.7rem] leading-tight text-[#1A2B22]">{b.title}</h3>
                <p className="mt-4 text-sm text-[#5C7365] leading-relaxed">{b.body}</p>
              </article>
            );
          })}
        </div>

        <div className="mt-20 grid md:grid-cols-12 gap-6 items-stretch">
          <div className="md:col-span-5 aspect-[4/5] rounded-3xl overflow-hidden border border-border">
            <img src={ABOUT_IMG} alt="Bamboo chasen whisking matcha"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
          </div>
          <div className="md:col-span-7 rounded-3xl bg-[#2C4C3B] text-[#F7F5F0] p-9 md:p-14 flex flex-col justify-between">
            <p className="overline text-[#EBEFDE]/80">Start small, earn trust, then scale.</p>
            <p className="font-serif text-3xl md:text-4xl leading-[1.1] mt-6">
              The slow-bar near Mall of Dehradun is a <em className="text-[#C47253]">beginning</em>, not a flagship —
              built to prove the model with real cups served to real regulars before it becomes anything bigger.
            </p>
            <p className="mt-8 text-sm text-[#DDE3D5] max-w-md">
              Two regions, two roles: Uji for matcha as the main event, Shizuoka for matcha as part of something
              else — and pricing that never pretends one is the other.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
